/**
 * `/raporty` — the trainer's forms: the survey list, the client × survey matrix, and the queue of
 * submissions waiting to be read.
 *
 * A wiring declaration. Which parameters this screen understands is the SLICE's decision, so the
 * validator comes from it — `?tab=kto` and `?tab=przegladu`, and no parameter means Ankiety.
 *
 * ── An unrecognised `?tab=` is DROPPED rather than defaulted into the URL ────────────────────
 * The rule and its reason are `_shell.klienci.$clientId.tsx`'s: writing a default back would rewrite
 * a link the trainer pasted, and the screen falls back on its own anyway. The validator also drops
 * the DEFAULT, so `/raporty` and `/raporty?tab=ankiety` do not become two addresses for one screen.
 *
 * ── The search is NEVER spread from the current one ──────────────────────────────────────────
 * `{ ...current, tab }` is what `_shell.klienci.$clientId.tsx` warns about at length: TanStack's
 * `applySearchMiddleware` spreads the validator's output OVER the updater's result rather than
 * replacing it, so an unknown key a link arrived with survives every tab press and is re-serialised
 * into the URL forever. `reportTabSearch` builds the whole object from a validated value.
 */

import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';

import { ReportsScreen, reportTabOf, reportTabSearch, validateReportsSearch } from '~/pages/reports';

export const Route = createFileRoute('/_shell/raporty/')({
  validateSearch: validateReportsSearch,
  component: ReportsRoute,
});

function ReportsRoute() {
  const search = useSearch({ from: '/_shell/raporty/' });
  const navigate = useNavigate();

  return (
    <ReportsScreen
      // `reportTabOf` runs AGAIN here, not only in `validateSearch`: the router merges the raw
      // search UNDER the validator's output, so a key the validator dropped still arrives —
      // `?tab=garbage` reaches this line as the literal string.
      tab={reportTabOf(search)}
      onSelectTab={(tab) => {
        // PUSHES rather than replaces: moving between tabs is navigation the trainer took, and Back
        // returning to the tab they came from is what a browser is for.
        void navigate({ to: '.', search: () => reportTabSearch(tab) });
      }}
    />
  );
}
